import { useState, useRef } from "react";
import type { SelectField } from "@/types/template";
import { autoAdvance } from "@/components/filler/fields/fieldUtils";

interface Props {
  field: SelectField;
  value: string;
  onChange: (value: unknown) => void;
  compact?: boolean;
}

function parseSelectedValue(
  value: string,
  options: string[],
): { selection: string; suffix: string } {
  for (const opt of options) {
    if (value === opt) {
      return { selection: opt, suffix: "" };
    }
    if (value.startsWith(opt + " ")) {
      return { selection: opt, suffix: value.slice(opt.length + 1) };
    }
  }
  return { selection: "", suffix: "" };
}

function SuffixInput({
  onSuffixChange,
  suffix,
}: Readonly<{ suffix: string; onSuffixChange: (v: string) => void }>) {
  return (
    <input
      type="text"
      className="select-suffix-input"
      value={suffix}
      onChange={(e) => {
        onSuffixChange(e.target.value);
      }}
      placeholder="additional notes..."
    />
  );
}

function CompactSelectView({
  onChange,
  onSuffixChange,
  selection,
  suffix,
  suffixVisible,
  value,
}: Readonly<{
  selection: string;
  value: string;
  suffix: string;
  suffixVisible: boolean;
  onChange: (value: unknown) => void;
  onSuffixChange: (v: string) => void;
}>) {
  return (
    <>
      <div className="select-tags">
        <button
          type="button"
          className="select-tag selected"
          onClick={() => {
            onChange("");
          }}
        >
          {selection || value}
        </button>
      </div>
      {suffixVisible && <SuffixInput suffix={suffix} onSuffixChange={onSuffixChange} />}
    </>
  );
}

interface FullSelectViewProps {
  field: SelectField;
  selection: string;
  value: string;
  showCustom: boolean;
  suffix: string;
  suffixVisible: boolean;
  onChange: (value: unknown) => void;
  onOptionClick: (opt: string, e: React.MouseEvent<HTMLButtonElement>) => void;
  onCustomToggle: () => void;
  onSuffixChange: (v: string) => void;
}

function SelectTagList({
  field,
  onCustomToggle,
  onOptionClick,
  selection,
  showCustom,
}: Readonly<
  Pick<
    FullSelectViewProps,
    "field" | "selection" | "showCustom" | "onOptionClick" | "onCustomToggle"
  >
>) {
  const containerRef = useRef<HTMLDivElement>(null);
  return (
    <div className="select-tags" ref={containerRef}>
      {field.options.map((opt) => (
        <button
          key={opt}
          type="button"
          className={`select-tag ${selection === opt && !showCustom ? "selected" : ""}`}
          onClick={(e) => {
            onOptionClick(opt, e);
          }}
        >
          {opt}
        </button>
      ))}
      <button
        type="button"
        className={`select-tag ${showCustom ? "selected" : ""}`}
        onClick={onCustomToggle}
      >
        Other...
      </button>
    </div>
  );
}

function FullSelectView(props: Readonly<FullSelectViewProps>) {
  const { onChange, onSuffixChange, showCustom, suffix, suffixVisible, value } = props;
  return (
    <>
      <SelectTagList
        field={props.field}
        selection={props.selection}
        showCustom={showCustom}
        onOptionClick={props.onOptionClick}
        onCustomToggle={props.onCustomToggle}
      />
      {showCustom && (
        <input
          type="text"
          className="select-custom-input"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          placeholder="Enter custom value..."
          autoFocus
        />
      )}
      {!showCustom && suffixVisible && (
        <SuffixInput suffix={suffix} onSuffixChange={onSuffixChange} />
      )}
    </>
  );
}

function useSelectState(value: string, options: string[]) {
  const { selection } = parseSelectedValue(value, options);
  const isCustom = value !== "" && selection === "";
  const [showCustom, setShowCustom] = useState(isCustom);
  const [suffix, setSuffix] = useState(() => parseSelectedValue(value, options).suffix);
  const [suffixVisible, setSuffixVisible] = useState(() => {
    const parsed = parseSelectedValue(value, options);
    return parsed.selection !== "" || parsed.suffix !== "";
  });
  return {
    selection,
    setShowCustom,
    setSuffix,
    setSuffixVisible,
    showCustom,
    suffix,
    suffixVisible,
  };
}

function useSelectHandlers(
  state: ReturnType<typeof useSelectState>,
  onChange: (value: unknown) => void,
) {
  const { selection, setShowCustom, setSuffix, setSuffixVisible, showCustom, suffix } = state;

  const handleCustomToggle = () => {
    setShowCustom(!showCustom);
    onChange("");
  };

  const handleOptionClick = (opt: string, e: React.MouseEvent<HTMLButtonElement>) => {
    setShowCustom(false);
    if (selection === opt) {
      onChange("");
    } else {
      onChange(suffix ? `${opt} ${suffix}` : opt);
      setSuffixVisible(true);
      autoAdvance(e.currentTarget);
    }
  };

  const handleSuffixChange = (newSuffix: string) => {
    setSuffix(newSuffix);
    if (selection) {
      onChange(newSuffix ? `${selection} ${newSuffix}` : selection);
    }
  };

  return { handleCustomToggle, handleOptionClick, handleSuffixChange };
}

export function SelectInput({ compact, field, onChange, value }: Readonly<Props>) {
  const state = useSelectState(value, field.options);
  const { handleCustomToggle, handleOptionClick, handleSuffixChange } = useSelectHandlers(
    state,
    onChange,
  );

  if (compact && value && !state.showCustom) {
    return (
      <CompactSelectView
        selection={state.selection}
        value={value}
        suffix={state.suffix}
        suffixVisible={state.suffixVisible}
        onChange={onChange}
        onSuffixChange={handleSuffixChange}
      />
    );
  }

  return (
    <FullSelectView
      field={field}
      selection={state.selection}
      value={value}
      showCustom={state.showCustom}
      suffix={state.suffix}
      suffixVisible={state.suffixVisible}
      onChange={onChange}
      onOptionClick={handleOptionClick}
      onCustomToggle={handleCustomToggle}
      onSuffixChange={handleSuffixChange}
    />
  );
}
